import { inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { LOCAL_STORAGE_KEYS } from '@shared/constants'
import { InjectableApi } from '@shared/decorators'
import { LocalStorageService } from '@shared/services/local-storage.service'
import { BehaviorSubject, Observable, Subject, map, startWith } from 'rxjs'
import { ApiService } from '../../shared/services/api.service'
import { ExtendedString } from '@shared/utils/util.types'
import { get } from '@shared/utils/util.functions'
import { LanguageTranslationResponseType, LatestTranslationsType } from '@shared/types'
import { ApiResponse } from '@entities/types'
import { DEFAULT_TRANSLATION_KEYS, TRANSLATION_CATEGORIES } from '@shared/constants/shared.const'

interface TranslateType {
  [key: string]: Record<string, string>
}

@InjectableApi({
  providedIn: 'root',
  baseSegment: '',
})
export class TranslationService extends ApiService {
  languageListChanged = new Subject()

  translateService = inject(TranslateService)
  localStorageService = inject(LocalStorageService)

  /** it having latest translation information */
  translations = new BehaviorSubject({
    lang: this.translateService.currentLang,
    translations: {},
  } as LatestTranslationsType)

  constructor() {
    super()

    // Update the translation data when the language changes
    this.getLatestTranslation().subscribe((value) => {
      this.translations.next(value)
    })
  }

  /** @function initialize will execute during the app initialization */
  initialize(defaultLanguage = this.getDefaultLanguage()): void {
    this.updateLanguage(defaultLanguage)
  }

  /** it will update the local storage */
  updateLanguage(newLanguage = this.getDefaultLanguage()) {
    const config = this.localStorageService.getData<Record<string, string>>(LOCAL_STORAGE_KEYS.LANGUAGE)
    // Save the language in local storage, based on the user ID
    this.localStorageService.saveData<Record<string, string>>(LOCAL_STORAGE_KEYS.LANGUAGE, {
      ...(config ?? {}),
      admin: newLanguage,
    })
    this.setTranslation(newLanguage, () => {
      // Emit event only after the translation has been set
      this.translateService.onLangChange.emit({
        lang: newLanguage,
        translations: this.translateService.store.translations[newLanguage],
      })
    })
  }

  /**  it will be return the default language, based on the user */
  getDefaultLanguage() {
    const config = this.localStorageService.getData<Record<string, string>>(LOCAL_STORAGE_KEYS.LANGUAGE)
    return config?.['admin'] ?? ''
  }

  /** Load translation from backend */
  loadTranslation(language: string): Observable<TranslateType> {
    return this.get<ApiResponse<LanguageTranslationResponseType>>(
      `/masters/language-matrix/language/text-data/${language}`,
    ).pipe(map((value) => value.data))
  }

  /** Set the translation data dynamically */
  setTranslation(language: string = this.getDefaultLanguage(), onComplete?: () => void): void {
    this.loadTranslation(language).subscribe((data) => {
      // Set the translation in ngx-translate
      this.translateService.setTranslation(language, data, true) // true: overwrite existing
      this.translateService.use(language) // Switch language

      // Call the completion callback after the translation is set
      if (onComplete) onComplete()
    })
  }

  /** Get translation asynchronously, in each changes it will be triggered */
  getTranslationAsync(key: string, context?: string): Observable<string> {
    const fullKey = context ? `${context}.${key}` : key
    const getTranslatedText = () => {
      const translation = this.translateService.instant(fullKey)
      return translation !== fullKey ? (translation?.length ? translation : key) : key // Return key if translation is missing
    }

    return this.translateService.onLangChange.pipe(startWith(getTranslatedText), map(getTranslatedText))
  }

  /** it will return translated text */
  public tr(key: ExtendedString<DEFAULT_TRANSLATION_KEYS>, context: ExtendedString<TRANSLATION_CATEGORIES>) {
    const translatedInfo = get(this.translations.getValue().translations, `${key}.${context}`)
    return translatedInfo?.length ? translatedInfo : key
  }

  /** you can get all translation from here */
  private getLatestTranslation(): Observable<LatestTranslationsType> {
    return this.translateService.onLangChange.pipe(
      // Start with the current language
      startWith({ lang: this.translateService.currentLang, translations: {}, tr: this.tr }),
      // Map the language change event to the translation data
      map((event) => {
        const currentLang = event.lang
        const translations = this.translateService.translations[currentLang]

        return {
          lang: currentLang,
          translations: translations as LanguageTranslationResponseType,
          tr: this.tr,
        }
      }),
    )
  }
}
